import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";

type PromptMode = "android" | "ios" | "desktop";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export default function MileageCalculator() {
  const [mpg, setMpg] = useState<number | "">("");
  const [rangeLeft, setRangeLeft] = useState<number | "">("");
  const [distance, setDistance] = useState<number | "">("");
  const [promptMode, setPromptMode] = useState<PromptMode | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const DISMISS_KEY = "vqs_install_prompt_dismissed";
    const wasDismissed = window.localStorage.getItem(DISMISS_KEY) === "1";
    const standaloneMode =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

    if (wasDismissed || standaloneMode) {
      return;
    }

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;

    if (isIOS) {
      setPromptMode("ios");
      setShowPrompt(true);
      return;
    }

    if (isAndroid) {
      setPromptMode("android");
      setShowPrompt(true);
    }

    if (!isMobile) {
      setPromptMode("desktop");
      setShowPrompt(true);
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
      setPromptMode("android");
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const dismissPrompt = () => {
    window.localStorage.setItem("vqs_install_prompt_dismissed", "1");
    setShowPrompt(false);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === "accepted") {
      setShowPrompt(false);
      window.localStorage.setItem("vqs_install_prompt_dismissed", "1");
    }

    setDeferredPrompt(null);
  };

  const parseNumber = (value: number | ""): number | null => {
    return value === "" || isNaN(value) ? null : value;
  };

  const parsedMpg = parseNumber(mpg);
  const parsedDistance = parseNumber(distance);
  const parsedRange = parseNumber(rangeLeft);

  const LITRES_PER_GALLON = 4.54609;

  const fuelRequired: string =
    parsedMpg && parsedDistance && parsedRange !== null
      ? (((parsedDistance - parsedRange) / parsedMpg) * LITRES_PER_GALLON).toFixed(1)
      : "0";

  const willMakeIt: boolean | null =
    parsedRange && parsedDistance
      ? parsedRange >= parsedDistance
      : null;

  const shortcutHint = /mac/i.test(window.navigator.platform) ? "Cmd+D" : "Ctrl+D";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 pb-24">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Fuel / Mileage Calculator</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mpg">Average MPG of vehicle</Label>
            <Input
              id="mpg"
              type="number"
              placeholder="e.g. 40"
              value={mpg}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setMpg(e.target.value === "" ? "" : Number(e.target.value))
              }
              min={0}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="range">Range Left (miles)</Label>
            <Input
              id="range"
              type="number"
              placeholder="e.g. 50"
              value={rangeLeft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setRangeLeft(e.target.value === "" ? "" : Number(e.target.value))
              }
              min={0}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="distance">Distance to Destination (miles)</Label>
            <Input
              id="distance"
              type="number"
              placeholder="e.g. 120"
              value={distance}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDistance(e.target.value === "" ? "" : Number(e.target.value))
              }
              min={0}
            />
          </div>

          <div className="mt-2 rounded-lg border border-border bg-muted p-4 flex flex-col gap-2">
            <p className="text-sm">
              <span className="font-semibold">Fuel Required:</span>{" "}
              {fuelRequired} litres
            </p>

            {willMakeIt !== null && (
              <p className="text-sm">
                <span className="font-semibold">Status:</span>{" "}
                <span className={willMakeIt ? "text-green-600" : "text-destructive"}>
                  {willMakeIt ? "You can reach the destination" : "You need fuel"}
                </span>
              </p>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-right">
            Authors: Jon P, Adam L
          </p>
        </CardContent>
      </Card>

      {showPrompt && promptMode && (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto w-full max-w-md rounded-xl border border-border bg-card p-4 shadow-lg">
          {promptMode === "android" && (
            <>
              <p className="text-sm font-semibold">Add this app to your Home Screen</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {deferredPrompt
                  ? "Install for one-tap access next time."
                  : "Open your browser menu and choose Add to Home screen."}
              </p>
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={dismissPrompt}
                  className="rounded-md border border-border px-3 py-1.5 text-sm"
                >
                  Not now
                </button>
                {deferredPrompt && (
                  <button
                    type="button"
                    onClick={handleAndroidInstall}
                    className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
                  >
                    Install
                  </button>
                )}
              </div>
            </>
          )}

          {promptMode === "ios" && (
            <>
              <p className="text-sm font-semibold">Add this app to your Home Screen</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Tap Share, then select Add to Home Screen.
              </p>
              <div className="mt-3 flex items-center justify-end">
                <button
                  type="button"
                  onClick={dismissPrompt}
                  className="rounded-md border border-border px-3 py-1.5 text-sm"
                >
                  Dismiss
                </button>
              </div>
            </>
          )}

          {promptMode === "desktop" && (
            <>
              <p className="text-sm font-semibold">Bookmark this page</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Press {shortcutHint} to add this calculator to your bookmarks.
              </p>
              <div className="mt-3 flex items-center justify-end">
                <button
                  type="button"
                  onClick={dismissPrompt}
                  className="rounded-md border border-border px-3 py-1.5 text-sm"
                >
                  Dismiss
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
